import { Button, Popover } from '@react-spectrum/s2'
import { useId } from 'react'
import type { CSSProperties, RefObject } from 'react'

export type SourcesCoachmarkPlacement = 'desktop_inline' | 'mobile_bottom'

export type SourcesCoachmarkRect = {
  top: number
  left: number
  right: number
  bottom: number
  width: number
  height: number
}

type SourcesCoachmarkProps = {
  placement: SourcesCoachmarkPlacement
  targetRect: SourcesCoachmarkRect
  targetRef: RefObject<HTMLElement | null>
  onDismiss: () => void
}

const SPOTLIGHT_GAP = 8

function getViewportSize() {
  if (typeof window === 'undefined') {
    return { width: 1024, height: 768 }
  }

  return {
    width: window.innerWidth || 1024,
    height: window.innerHeight || 768,
  }
}

function Spotlight({ targetRect }: { targetRect: SourcesCoachmarkRect }) {
  const viewport = getViewportSize()
  const top = Math.max(targetRect.top - SPOTLIGHT_GAP, 0)
  const left = Math.max(targetRect.left - SPOTLIGHT_GAP, 0)
  const right = Math.min(targetRect.right + SPOTLIGHT_GAP, viewport.width)
  const bottom = Math.min(targetRect.bottom + SPOTLIGHT_GAP, viewport.height)
  const overlayClass =
    'pointer-events-none fixed z-40 bg-black/10 transition-opacity duration-200 dark:bg-black/20'

  return (
    <>
      <div
        aria-hidden="true"
        data-testid="sources-coachmark-spotlight"
        className={overlayClass}
        style={{ left: 0, right: 0, top: 0, height: top }}
      />
      <div
        aria-hidden="true"
        data-testid="sources-coachmark-spotlight"
        className={overlayClass}
        style={{ left: 0, width: left, top, height: bottom - top }}
      />
      <div
        aria-hidden="true"
        data-testid="sources-coachmark-spotlight"
        className={overlayClass}
        style={{ left: right, right: 0, top, height: bottom - top }}
      />
      <div
        aria-hidden="true"
        data-testid="sources-coachmark-spotlight"
        className={overlayClass}
        style={{ left: 0, right: 0, top: bottom, bottom: 0 }}
      />
    </>
  )
}

function CoachmarkContent({
  testId,
  titleId,
  descriptionId,
  onDismiss,
}: {
  testId: string
  titleId: string
  descriptionId: string
  onDismiss: () => void
}) {
  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      data-testid={testId}
      className="w-full max-w-[22rem] p-4 text-left"
    >
      <p
        id={titleId}
        className="text-sm font-semibold leading-snug text-foreground"
      >
        Source-backed text is highlighted
      </p>
      <p
        id={descriptionId}
        className="mt-1.5 text-xs font-semibold leading-relaxed text-foreground"
      >
        Tap any highlight to inspect the source.
      </p>
      <div className="mt-3 flex justify-end">
        <Button size="S" variant="primary" onPress={onDismiss}>
          <span className="whitespace-nowrap">Got it</span>
        </Button>
      </div>
    </div>
  )
}

export function SourcesCoachmark({
  placement,
  targetRect,
  targetRef,
  onDismiss,
}: SourcesCoachmarkProps) {
  const titleId = useId()
  const descriptionId = useId()
  const isDesktopPlacement = placement === 'desktop_inline'

  return (
    <>
      <Spotlight targetRect={targetRect} />
      {isDesktopPlacement ? (
        <Popover
          triggerRef={targetRef}
          isOpen
          onOpenChange={(isOpen) => {
            if (!isOpen) onDismiss()
          }}
          placement="bottom start"
          offset={10}
          containerPadding={16}
          shouldFlip
          size="S"
        >
          <CoachmarkContent
            testId="sources-coachmark-desktop"
            titleId={titleId}
            descriptionId={descriptionId}
            onDismiss={onDismiss}
          />
        </Popover>
      ) : (
        <div
          className="fixed left-4 right-4 z-50 rounded-2xl border border-border/80 bg-background text-left shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-200"
          style={
            {
              bottom:
                'calc(var(--chat-composer-height, 9rem) + env(safe-area-inset-bottom) + var(--chat-keyboard-inset, 0px) + 0.75rem)',
            } satisfies CSSProperties
          }
        >
          <CoachmarkContent
            testId="sources-coachmark-mobile"
            titleId={titleId}
            descriptionId={descriptionId}
            onDismiss={onDismiss}
          />
        </div>
      )}
    </>
  )
}
